import { mutateRoutingBoms } from "@/api/routingBoms/useRoutingBomsQuery";
import { BomByOperationCreateForm } from "@/features/bomSearch/table/form/BomByOperationCreateForm";
import { BomByOperationUpdateForm } from "@/features/bomSearch/table/form/BomByOperationUpdateForm";
import { useModal } from "@/features/modal/ModalStackManager";
import customAlert from "@/features/ui/alert/alert";
import { DetailLink } from "@/features/ui/detail/DetailLink";
import { theme } from "@/styles/theme";
import { setToLocaleString } from "@/utils/unitMark";
import styled from "@emotion/styled";
import { Button, Table, Text, useMantineColorScheme } from "@mantine/core";
import { DefaultApiRoutingBomsRoutingBomIdDeleteRequest, ItemsGet200ResponseRowsInnerRoutingsInnerRoutingBomsInner } from "@sizlcorp/sizl-api-document/dist/models";
import { IconEdit, IconPlus, IconTrash } from "@tabler/icons-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { ItemsDetailForm } from "../../ItemsDetailForm";

const TableContainer = styled(Table)``;

const Thead = styled.thead`
  display: flex;
  width: 100%;
  align-items: flex-start;
  align-self: stretch;
`;

const Th = styled.th<{ width?: number }>`
  display: flex;
  width: ${(props) => (props.width ? `${props.width}%` : "auto")};
  padding: 7px 10px;
  justify-content: center;
  align-items: center;
  gap: 10px;
`;

const Tr = styled.tr`
  display: flex;
  width: 100%;
  align-items: center;
`;

const Tbody = styled.tbody`
  display: flex;
  flex-direction: column;
  width: 100%;
  align-items: flex-start;
  align-self: stretch;
`;

const Td = styled.td<{ width?: number }>`
  display: flex;
  width: ${(props) => (props.width ? `${props.width}%` : "auto")};
  height: 100%;
  padding: 7px 10px;
  align-items: flex-start;
  gap: 10px;
`;

const TextNumber = styled<any>(Text)`
  width: 100%;
`;

export const RoutingBomRow = (params: {
  routingCode?: string | undefined;
  routingBoms: ItemsGet200ResponseRowsInnerRoutingsInnerRoutingBomsInner[];
}) => {
  const { routingBoms, routingCode } = params;
  const { openModal } = useModal();
  const { colorScheme, toggleColorScheme } = useMantineColorScheme();
  const isDarkMode: boolean = colorScheme === "light";

  const queryClient = useQueryClient();

  const { mutate: deleteMutate } = useMutation(
    (params: DefaultApiRoutingBomsRoutingBomIdDeleteRequest) =>
      mutateRoutingBoms.delete(params).mutationFn(undefined),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["items"]);
        customAlert('삭제되었습니다.', '성공', 'green');
      },
      onError: (error: AxiosError<Error>) => {
        customAlert(
          error.response?.data?.message ?? "Bom 삭제에 실패하였습니다.",
          "등록 실패",
          "red"
        )
      }
    }
  );

  const openEditModal = ({ routingBom }: { routingBom: ItemsGet200ResponseRowsInnerRoutingsInnerRoutingBomsInner }) => {
    openModal(
      <BomByOperationUpdateForm data={routingBom} />,
      null,
      "BOM 수정"
    );
  }

  const openCreateModal = ({ routingCode }: { routingCode: string | undefined }) => {
    openModal(
      <BomByOperationCreateForm routingCode={routingCode} />,
      null,
      "BOM 추가"
    );
  };

  const handleDeleteBom = ({ routingBomId }: { routingBomId: number | undefined }) => {
    if (routingBomId === undefined) return;
    deleteMutate({
      routingBomId: routingBomId
    })
  }


  return (
    <TableContainer>
      <Thead>
        <Tr>
          <Th width={20}>
            <Text>시퀀스</Text>
          </Th>
          <Th width={50}>원부자재</Th>
          <Th width={30}>수량</Th>
          <Th width={15}>액션</Th>
        </Tr>
      </Thead>
      <Tbody>
        {routingBoms &&
          routingBoms.map(
            (
              routingBom: ItemsGet200ResponseRowsInnerRoutingsInnerRoutingBomsInner,
              i: number
            ) =>
              routingBom.seq &&
              routingBom.itemCode &&
              routingBom.quantity && (
                <Tr key={i}>
                  <Td width={20}>
                    <TextNumber ta="right">{routingBom.seq}</TextNumber>
                  </Td>
                  <Td width={50}>
                    <DetailLink onClick={() => openModal(
                      <ItemsDetailForm itemCode={routingBom.itemCode as string} />,
                      null,
                      "품목 상세"
                    )}>
                      {routingBom?.item?.name ?? routingBom?.itemName ?? '-'}({routingBom.itemCode})
                      {/* <IconLink /> */}
                      {/* <IconLinkPlus /> */}
                    </DetailLink>
                    {/* <NavLink
                    label={routingBom.itemCode}
                    icon={<IconLink size="1rem" stroke={1.5} />}
                  /> */}
                  </Td>
                  <Td width={30}>
                    <TextNumber ta="right">
                      {setToLocaleString(routingBom.quantity ?? "")} {routingBom.item?.unit_text}
                    </TextNumber>
                  </Td>
                  <Td width={15} >
                    <Button
                      w={32}
                      variant="subtle"
                      onClick={() => openEditModal({ routingBom })}
                      leftIcon={<IconEdit />}
                      color="yellow"
                    ></Button>
                    <Button
                      w={32}
                      variant="subtle"
                      onClick={() => handleDeleteBom({ routingBomId: routingBom.id })}
                      leftIcon={<IconTrash />}
                      color="red"
                    ></Button>
                  </Td>
                </Tr>
              )
          )}
        <Tr>
          <Td style={{ width: '100%', justifyContent: 'center' }}>
            <Button
              leftIcon={<IconPlus />}
              variant={'subtle'}
              color={isDarkMode ? "dark" : "white"}
              onClick={() => openCreateModal({ routingCode })}
            >
              새로운 BOM 추가
            </Button>
          </Td>
        </Tr>
      </Tbody>
    </TableContainer >
  );
};

const Link = styled.a`
  color: ${theme.colors?.blue?.[7]};
  text-decoration: none;
  font-size: 14px;
  cursor: pointer;
`;
